// SuperNarrateView — Netflix/Prime-style genre picker → language → generate.
// The active player layers three audio streams: the main ElevenLabs TTS,
// a looping ambient bed, and a short intro sting that plays at chapter start.
// Backend handles genre → voice + script preset + SFX caching.
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Mic, Play, Pause, SkipForward, SkipBack, Square, Download, ChevronLeft, Sparkles, Volume2, VolumeX, Share2, ArrowRight, Check, Rewind, FastForward, Wand2, AlertTriangle } from 'lucide-react';
import { InlineSparkle } from '../../../../components/Loader';
import { featureFill, featureInk } from './featureColors';
import { toast } from 'sonner';
import GenerationsShell from './GenerationsShell';
import AutoTunedHeader from './AutoTunedHeader';
import TopicChipPicker from './TopicChipPicker';
import { Hero } from './MagicDeskView';
import { useStudent } from '../../StudentApp';
import GENRE_ART from './genreArt';
import { friendlyError } from '../../../../lib/errors';

// ── Kids-mode auto-detection ─────────────────────────────────────────────────
// Looks at the student's profile (DOB-derived age preferred, age_band fallback)
// and decides (a) whether to promote the Kids tab as the default audience and
// (b) which age-band card to pre-highlight.
function detectKidsRecommendation(profile) {
  if (!profile) return { recommendKids: false, suggestedBandId: null, ageYears: null };
  let ageYears = null;
  if (profile.dob) {
    try {
      const dob = new Date(profile.dob);
      if (!isNaN(dob.getTime())) {
        ageYears = (Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000);
      }
    } catch (_) { /* ignore */ }
  }
  // Decide band from age first, then fall back to age_band
  if (ageYears != null) {
    if (ageYears < 6)  return { recommendKids: true, suggestedBandId: 'pre_k',         ageYears };
    if (ageYears < 8)  return { recommendKids: true, suggestedBandId: 'primary',       ageYears };
    if (ageYears < 11) return { recommendKids: true, suggestedBandId: 'upper_primary', ageYears };
    if (ageYears < 13) return { recommendKids: true, suggestedBandId: 'upper_primary', ageYears };
    return { recommendKids: false, suggestedBandId: null, ageYears };
  }
  if (profile.age_band === 'under-13') {
    return { recommendKids: true, suggestedBandId: 'upper_primary', ageYears: null };
  }
  return { recommendKids: false, suggestedBandId: null, ageYears: null };
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TINT = '#0EA5E9';
const TINT_FILL = featureFill(TINT); // sky is too light for white text on its own
const TINT_INK = featureInk(TINT);

const DURATIONS = [
  { id: 'short',  label: 'Short',  hint: '~3 min' },
  { id: 'medium', label: 'Medium', hint: '~5 min' },
  { id: 'long',   label: 'Long',   hint: '~8 min' },
];

const tok = () => localStorage.getItem('desky_token') || localStorage.getItem('access_token');

export default function SuperNarrateView({ lessonId, lessonTitle }) {
  return (
    <GenerationsShell
      lessonId={lessonId}
      kind="narrate"
      kindLabel="Super Narrate"
      tint={TINT}
      defaultCost={15}
      showCreditsChip={false}
      emptyHint="Listen to this lesson as a story in any genre, any language."
      setupPanel={({ onGenerate, generating }) => (
        <Setup lessonId={lessonId} lessonTitle={lessonTitle}
          onGenerate={(p) => {
            // Two narration paths share the same backend genre engine:
            //   __kind === 'concept'  → universal "Explain this lesson with
            //                            examples" preset (genre=explanation)
            //   default              → the chosen genre-driven audiobook
            const path = `/student/lesson/${lessonId}/narrate/generate`;
            const { __kind, ...payload } = p;
            return onGenerate(path, payload);
          }}
          generating={generating} />
      )}
      renderBody={(gen, ctx) => <Player gen={gen} ctx={ctx} />}
    />
  );
}

// ── Setup: 3-step flow ─────────────────────────────────────────────────────
function Setup({ lessonId, lessonTitle, onGenerate, generating }) {
  const studentCtx = useStudent();
  const profile = studentCtx?.me?.student_profile || {};
  const kidsHint = useMemo(() => detectKidsRecommendation(profile), [profile.dob, profile.age_band]);
  const [autotuned, setAutotuned] = useState(true);
  const [step, setStep] = useState(1); // 1 = audience+genre/band, 2 = language, 3 = confirm
  const [audience, setAudience] = useState(kidsHint.recommendKids ? 'kids' : 'standard');
  const [genres, setGenres] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [kidsBands, setKidsBands] = useState([]);
  const [genre, setGenre] = useState(null);
  const [kidsBand, setKidsBand] = useState(null);
  const [language, setLanguage] = useState(null);
  const [duration, setDuration] = useState('medium');
  const [explainerLangModalOpen, setExplainerLangModalOpen] = useState(false);
  // We only want to apply the auto-default ONCE per mount so a student can
  // freely toggle to Standard without us snapping them back to Kids.
  const autoDefaultedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const [g, l, a] = await Promise.all([
          axios.get(`${API}/student/narrate/genres`,    { headers: { Authorization: `Bearer ${tok()}` } }),
          axios.get(`${API}/student/narrate/languages`, { headers: { Authorization: `Bearer ${tok()}` } }),
          axios.get(`${API}/student/narrate/audiences`, { headers: { Authorization: `Bearer ${tok()}` } }),
        ]);
        setGenres(g.data?.genres || []);
        setLanguages(l.data?.languages || []);
        setKidsBands(a.data?.kids_bands || []);
      } catch { /* shell will retry on next mount */ }
    })();
  }, []);

  // Pre-select English as the default language
  useEffect(() => {
    if (!language && languages.length) setLanguage(languages.find(x => x.id === 'en') || languages[0]);
  }, [languages, language]);

  // Auto-pre-select the kid's recommended band the first time the bands arrive
  // for a kid-aged student. Doesn't auto-advance the step — the kid still sees
  // the cards highlighted with their match so they feel in control.
  useEffect(() => {
    if (autoDefaultedRef.current) return;
    if (!kidsHint.recommendKids || !kidsBands.length) return;
    const match = kidsBands.find(b => b.id === kidsHint.suggestedBandId);
    if (match) {
      setKidsBand(match);
      autoDefaultedRef.current = true;
    }
  }, [kidsBands, kidsHint.recommendKids, kidsHint.suggestedBandId]);

  // Switching audience clears the previous selection so the user always picks
  // fresh and we don't accidentally generate Comedy when they meant Kids.
  const switchAudience = (a) => {
    setAudience(a);
    setGenre(null);
    setKidsBand(null);
  };

  const onPickGenre = (g) => { setGenre(g);     setStep(2); };
  const onPickBand  = (b) => { setKidsBand(b);  setStep(2); };

  const ready = audience === 'kids' ? !!kidsBand : !!genre;
  const heroForConfirm = audience === 'kids'
    ? {
        // Faux-genre object used to render the Confirm hero in kids mode
        id: 'kids', label: 'Kids Zone',
        emoji: kidsBand?.emoji || '🧒',
        gradient: ['#F59E0B', '#EC4899'],
        accent: kidsBand?.accent || '#F59E0B',
        blurb: kidsBand?.blurb || 'Cartoon explainer tuned to your age band.',
        _kids_band_label: kidsBand?.label,
      }
    : genre;

  return (
    <div data-testid="narrate-setup-panel">
      <SetupHeader step={step}
        audience={audience} kidsBand={kidsBand}
        genre={genre} language={language}
        onBack={() => setStep(Math.max(1, step - 1))} lessonTitle={lessonTitle} />
      <AutoTunedHeader lessonId={lessonId} kind="narrate" tint={TINT}
        autotuned={autotuned} onChange={setAutotuned}
        testIdPrefix="narrate-autotune"
        hideToggle
        quickActionLabel="Generate Now"
        quickActionDisabled={generating}
        onQuickAction={() => {
          // One-tap path: backend picks the angle silently. If the student
          // hasn't picked a genre yet, default to 'explanation' — the
          // most universally-pleasant narration style.
          const fallbackGenre = (genres.find(g => g.id === 'explanation') || genres[0])?.id || 'explanation';
          const lang = (language || languages.find(x => x.id === 'en') || languages[0])?.id || 'en';
          if (audience === 'kids') {
            const band = (kidsBand || kidsBands.find(b => b.id === (kidsHint.suggestedBandId || 'primary')) || kidsBands[0]);
            if (!band) return;
            onGenerate({ audience: 'kids', kids_band: band.id, language: lang, duration: 'medium', autotuned: true });
          } else {
            const g = (genre?.id) || fallbackGenre;
            onGenerate({ genre: g, language: lang, duration: 'medium', autotuned: true });
          }
        }}
      />

      {step === 1 && (
        <>
          {/* SuperNarrate Concept — compact featured pill. The long
              description used to live inline; it now sits behind a tiny
              (i) info button so the card stays tight and aesthetic. */}
          <div
            data-testid="super-narrate-concept-cta-row"
            className="relative w-full mb-5 overflow-hidden flex items-center gap-3"
            style={{
              borderRadius: 12,
              background:   'var(--st-card)',
              border:      `1.5px solid var(--st-border)`,
              boxShadow:    '0 4px 14px -10px rgba(15,23,42,0.18)',
              padding:      '10px 14px',
            }}>
            <span aria-hidden="true" className="absolute top-0 left-0 bottom-0 w-1"
              style={{ background: TINT }} />

            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{
                background: `color-mix(in srgb, ${TINT} 14%, transparent)`,
                color:      TINT_INK,
              }}>
              <Sparkles className="w-4 h-4" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold "
                  style={{ color: TINT_INK }}>
                  SuperNarrate Concept
                </span>
              </div>
              <div className="text-sm sm:text-[14px] font-semibold leading-tight truncate"
                style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
                Hear the entire concept — explained with examples.
              </div>
            </div>

            <button
              onClick={() => setExplainerLangModalOpen(true)}
              disabled={generating}
              data-testid="super-narrate-concept-cta"
              className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-full transition-transform  disabled:opacity-60"
              style={{
                background: TINT_FILL,
                color:      '#fff',
                boxShadow:  `0 6px 16px -8px ${TINT}88`,
              }}>
              {generating && <InlineSparkle size={14} label="Working" />}
              {generating ? 'Working' : 'Explain'}
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          {/* <GenreHeroArt /> */}
          <AudienceTabs audience={audience} onChange={switchAudience} kidsHint={kidsHint} />
          {audience === 'standard'
            ? <GenreGrid genres={genres} selected={genre} onSelect={onPickGenre} />
            : <KidsBandGrid bands={kidsBands} selected={kidsBand} onSelect={onPickBand}
                suggestedBandId={kidsHint.suggestedBandId} />}
        </>
      )}
      {step === 2 && (
        <LanguageGrid languages={languages} selected={language} onSelect={(l) => { setLanguage(l); setStep(3); }} />
      )}
      {step === 3 && ready && language && (
        <Confirm
          genre={heroForConfirm}
          language={language}
          duration={duration}
          setDuration={setDuration}
          generating={generating}
          ctaLabel={audience === 'kids' ? `Generate Kids audiobook` : `Generate ${heroForConfirm.label} audiobook`}
          onGenerate={() => onGenerate(
            audience === 'kids'
              ? { audience: 'kids', kids_band: kidsBand.id, language: language.id, duration, autotuned }
              : { genre: genre.id, language: language.id, duration, autotuned }
          )}
        />
      )}

      {/* Language modal — opens when the SuperNarrate Concept CTA is tapped */}
      {explainerLangModalOpen && (
        <SuperNarrateLangModal
          languages={languages}
          onClose={() => setExplainerLangModalOpen(false)}
          onPick={(lid) => {
            setExplainerLangModalOpen(false);
            // SuperNarrate Concept = the existing narrate pipeline pinned to
            // the universal "explanation" genre with autotune so the AI
            // grounds it in real examples. Duration scales naturally with
            // the concept's content rather than being capped — we pass
            // 'long' so the script supervisor isn't forced to truncate
            // deeper lessons.
            onGenerate({
              __kind:    'concept',
              genre:     'explanation',
              language:  lid,
              duration:  'long',
              autotuned: true,
            });
          }}
        />
      )}
    </div>
  );
}

// Slim language modal shared with the Visual Explainer CTA. Mirrors the
// shape used in DeskeynomicsView so the two flows feel identical.
function SuperNarrateLangModal({ languages, onPick, onClose }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState('en');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const q = query.trim().toLowerCase();
  const filtered = !q ? languages : languages.filter(l =>
    (l.label || '').toLowerCase().includes(q) ||
    (l.native || '').toLowerCase().includes(q) ||
    (l.id || '').toLowerCase().includes(q)
  );
  return (
    <div
      data-testid="super-narrate-language-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(8,12,24,0.66)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}>
      <div className="w-full max-w-xl rounded-3xl border shadow-lg overflow-hidden"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3 flex items-center gap-3 border-b" style={{ borderColor: 'var(--st-border)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white"
            style={{ background: `linear-gradient(135deg, ${TINT_FILL} 0%, color-mix(in srgb, ${TINT} 50%, #000) 100%)` }}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold " style={{ color: TINT_INK }}>
              SuperNarrate Concept · Language
            </div>
            <div className="text-[15px] font-semibold leading-tight" style={{ color: 'var(--st-ink)' }}>
              Which language should the concept be explained in?
            </div>
          </div>
          <button onClick={onClose}
            data-testid="super-narrate-language-modal-close"
            className="w-8 h-8 rounded-full flex items-center justify-center border"
            style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)' }}>×</button>
        </div>
        <div className="p-3 border-b" style={{ borderColor: 'var(--st-border)' }}>
          <input
            data-testid="super-narrate-language-modal-search"
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search 29 ElevenLabs languages…"
            className="w-full text-sm px-3 py-2 rounded-xl border outline-none"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)', color: 'var(--st-ink)' }} />
        </div>
        <div className="px-3 py-2 max-h-[44vh] overflow-y-auto grid grid-cols-2 gap-1.5">
          {filtered.map(l => (
            <button key={l.id} onClick={() => setSelected(l.id)}
              data-testid={`super-narrate-language-modal-opt-${l.id}`}
              aria-pressed={selected === l.id}
              className="px-3 py-2 rounded-xl border text-left inline-flex items-center gap-2"
              style={{
                borderColor: selected === l.id ? TINT : 'var(--st-border)',
                background: selected === l.id ? `color-mix(in srgb, ${TINT} 14%, var(--st-card))` : 'var(--st-card)',
              }}>
              <span className="text-base">{l.flag}</span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold truncate" style={{ color: 'var(--st-ink)' }}>{l.native}</span>
                <span className="block text-xs truncate" style={{ color: 'var(--st-mute)' }}>{l.label}</span>
              </span>
              {selected === l.id && <Check className="w-3.5 h-3.5 ml-auto" style={{ color: TINT_INK }} />}
            </button>
          ))}
        </div>
        <div className="px-5 py-4 flex justify-end border-t" style={{ borderColor: 'var(--st-border)' }}>
          <button onClick={() => onPick?.(selected)}
            data-testid="super-narrate-language-modal-confirm"
            className="rounded-full px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-1.5"
            style={{ background: TINT_FILL, boxShadow: `0 12px 28px -12px ${TINT}` }}>
            <Sparkles className="w-3.5 h-3.5" /> Begin Concept narration
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── GenreHeroArt ────────────────────────────────────────────────────────
// One static, hand-built spot illustration for the genre/band picker —
// NOT AI-generated, NOT per-genre. Renders identically for every student,
// every genre, every session. Flat geometric shapes + a limited palette,
// matching the "Choose a flavour" studio framing without depicting any
// one genre specifically.
function GenreHeroArt() {
  return (
    <div className="relative mb-6 overflow-hidden"
      data-testid="narrate-genre-hero-art"
      style={{
        borderRadius: 18,
        background:   'var(--st-card)',
        border:      `1.5px solid var(--st-border)`,
        boxShadow:    '0 8px 24px -16px rgba(15,23,42,0.18)',
      }}>
      <svg viewBox="0 0 640 200" className="w-full h-auto block" role="img"
        aria-label="Deskey Audiobook Studio">
        {/* flat color-block backdrop */}
        <rect x="0" y="0" width="640" height="200" fill="#F4F1EA" />
        <rect x="360" y="0" width="280" height="200" fill="#DCEEFB" />
        <rect x="470" y="30" width="170" height="170" rx="18" fill="#FFE3B0" />
        <circle cx="600" cy="40" r="22" fill="#FF6B5B" />

        {/* arch frame */}
        <path d="M140 190 V110 A100 100 0 0 1 340 110 V190 Z" fill="#0EA5E9" />

        {/* open book — two overlapping pages on a spine */}
        <path d="M180 168 V118 Q216 104 240 118 V168 Q216 156 180 168 Z" fill="#0B1220" />
        <path d="M300 168 V118 Q264 104 240 118 V168 Q264 156 300 168 Z" fill="#0B1220" />
        <path d="M186 162 V124 Q214 113 234 123 V163 Q212 154 186 162 Z" fill="#F7F3EA" />
        <path d="M294 162 V124 Q266 113 246 123 V163 Q268 154 294 162 Z" fill="#F7F3EA" />

        {/* soundwave rising from the spine — reads as "narrate" */}
        <rect x="252" y="70" width="10" height="34" rx="5" fill="#FF6B5B" />
        <rect x="268" y="54" width="10" height="50" rx="5" fill="#FFE3B0" />
        <rect x="284" y="66" width="10" height="38" rx="5" fill="#FF6B5B" />
        <rect x="300" y="46" width="10" height="58" rx="5" fill="#FFE3B0" />

        {/* small decorative dot accents */}
        <circle cx="120" cy="50" r="10" fill="#F6B93B" />
        <circle cx="100" cy="80" r="6" fill="#0EA5E9" />
      </svg>
    </div>
  );
}

// ─── AudienceTabs ────────────────────────────────────────────────────────
function AudienceTabs({ audience, onChange, kidsHint }) {
  const items = [
    { id: 'standard', label: 'Standard',  hint: 'All 16 genres' },
    { id: 'kids',     label: 'For Kids',  hint: 'Cartoon explainer' },
  ];
  return (
    <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-b"
      style={{ borderColor: 'var(--st-border)' }}
      data-testid="narrate-audience-tabs">
      {items.map(it => {
        const isOn = audience === it.id;
        const isRecommended = it.id === 'kids' && kidsHint?.recommendKids;
        return (
          <button key={it.id} onClick={() => onChange(it.id)}
            data-testid={`narrate-audience-${it.id}`}
            data-recommended={isRecommended ? 'true' : undefined}
            aria-pressed={isOn}
            className="relative pb-3 pt-1 inline-flex items-baseline gap-2 transition-colors">
            <span className="text-sm font-semibold "
              style={{ color: isOn ? 'var(--st-ink)' : 'var(--st-mute)' }}>
              {it.label}
            </span>
            <span className="text-xs font-semibold hidden sm:inline"
              style={{ color: 'var(--st-mute)', opacity: 0.7 }}>
              {it.hint}
            </span>
            {isRecommended && (
              <span data-testid="narrate-kids-recommended-pill"
                className="text-xs font-semibold px-1.5 py-0.5 rounded"
                style={{ background: TINT_FILL, color: '#fff' }}>
                For you
              </span>
            )}
            <span aria-hidden="true"
              className="absolute left-0 right-0 -bottom-px h-[2px] transition-opacity"
              style={{ background: TINT, opacity: isOn ? 1 : 0 }} />
          </button>
        );
      })}
      {kidsHint?.recommendKids && (
        <p className="text-xs ml-auto pb-2" style={{ color: 'var(--st-mute)' }}
          data-testid="narrate-kids-recommendation-note">
          Defaulted to Kids — your profile says
          {kidsHint.ageYears != null ? ` you're ${Math.floor(kidsHint.ageYears)}` : " you're under 13"}.
        </p>
      )}
    </div>
  );
}

function SetupHeader({ step, audience, kidsBand, genre, language, onBack, lessonTitle }) {
  const labels = audience === 'kids'
    ? ['Age Band', 'Language', 'Master']
    : ['Genre',    'Language', 'Master'];
  const crumbs = ['Studio', 'Commission', labels[step - 1]];
  return (
    <div className="mb-5" data-testid="narrate-step-header">
      <div className="flex items-center gap-2 mb-2 text-xs font-semibold "
        style={{ color: 'var(--st-mute)' }}>
        {step > 1 && (
          <button onClick={onBack} data-testid="narrate-step-back"
            className="w-6 h-6 rounded-full border flex items-center justify-center transition mr-1"
            style={{ borderColor: 'var(--st-border)', color: 'var(--st-ink)' }}>
            <ChevronLeft className="w-3 h-3" />
          </button>
        )}
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            <span style={{ color: i === crumbs.length - 1 ? TINT_INK : 'var(--st-mute)' }}>{c}</span>
            {i < crumbs.length - 1 && <span style={{ opacity: 0.4 }}>/</span>}
          </React.Fragment>
        ))}
      </div>
      <div className="py-3">
        <div className="text-xs font-semibold "
          style={{ color: TINT_INK }}>
          Deskey Audiobook Studio · Now Casting
        </div>
        <h2 className="text-[26px] sm:text-[32px] font-semibold leading-[1.05] mt-1.5 max-w-3xl"
          style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
          &ldquo;{lessonTitle}&rdquo;
        </h2>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2.5 text-xs font-semibold"
          style={{ color: 'var(--st-mute)' }}>
          {audience === 'kids' && kidsBand && step > 1 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-1 h-1 rounded-full" style={{ background: TINT }} />
              {kidsBand.label}
            </span>
          )}
          {audience !== 'kids' && genre && step > 1 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-1 h-1 rounded-full" style={{ background: TINT }} />
              {genre.label}
            </span>
          )}
          {language && step > 2 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-1 h-1 rounded-full" style={{ background: TINT }} />
              {language.native}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function GenreGrid({ genres, selected, onSelect }) {
  return (
    <div data-testid="narrate-genre-grid">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="text-[15px] sm:text-[17px] font-semibold "
          style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
          Choose a flavour
        </h3>
        <p className="text-xs font-semibold "
          style={{ color: 'var(--st-mute)' }}>
          {genres.length} genres
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {genres.map(g => (
          <PosterCard
            key={g.id}
            id={g.id}
            label={g.label}
            blurb={g.blurb}
            accent={g.accent || g.gradient?.[0] || TINT}
            selected={selected?.id === g.id}
            badge="Genre"
            testId={`narrate-genre-${g.id}`}
            onClick={() => onSelect(g)}
          />
        ))}
      </div>
    </div>
  );
}

function KidsBandGrid({ bands, selected, onSelect, suggestedBandId }) {
  return (
    <div data-testid="narrate-kids-grid">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="text-[15px] sm:text-[17px] font-semibold "
          style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
          Pick your reading level
        </h3>
        <p className="text-xs font-semibold "
          style={{ color: 'var(--st-mute)' }}>
          {bands.length} bands
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {bands.map(b => {
          const isSuggested = suggestedBandId && b.id === suggestedBandId;
          return (
            <PosterCard
              key={b.id}
              id={b.id}
              label={b.label}
              blurb={b.blurb}
              accent={b.accent || TINT}
              selected={selected?.id === b.id}
              badge={isSuggested ? 'Your match' : 'Reading band'}
              testId={`narrate-kids-band-${b.id}`}
              testIdAttrs={isSuggested ? { 'data-suggested': 'true' } : {}}
              onClick={() => onSelect(b)}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Poster card — renders a lesson-aware AI cover inside the box. ────────
// Falls back to a soft accent wash + shimmer while the backend is still
// rendering the artwork. Identical chrome shared by the standard genre
// grid, the kids-band grid and the Video Studio GenrePickerModal.
function PosterCard({
  id, label, blurb, accent, selected, badge,
  testId, testIdAttrs = {}, onClick, aspect = '1 / 1',
}) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-pressed={selected}
      {...testIdAttrs}
      className="group relative text-left overflow-hidden transition duration-300 focus:outline-none"
      style={{
        aspectRatio:  aspect,
        borderRadius: 14,
        background:   'var(--st-card)',
        border:      `1.5px solid ${selected ? TINT : 'var(--st-border)'}`,
        boxShadow:    selected
          ? `0 16px 32px -16px ${TINT}66, 0 0 0 3px ${TINT}22`
          : '0 4px 14px -10px rgba(15,23,42,0.18)',
      }}>
      {GENRE_ART[id] ? (
        <img src={GENRE_ART[id]} alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          loading="lazy"
          draggable={false} />
      ) : (
        <div className="absolute inset-0"
          style={{
            background: `linear-gradient(155deg, ${accent} 0%, color-mix(in srgb, ${accent} 35%, var(--st-card)) 100%)`,
          }} />
      )}

      <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(8,8,14,0.82) 75%, rgba(8,8,14,0.96) 100%)' }} />

      {/* Top-right — selected check only. We dropped the "Genre" / "Reading
          band" pill the user asked us to remove; the bottom title block
          already names the card. */}
      <div className="relative z-10 flex items-start justify-end gap-2 p-2.5 min-h-[28px]">
        {selected && (
          <span className="w-5 h-5 rounded-full inline-flex items-center justify-center"
            style={{ background: TINT_FILL, color: '#fff' }}>
            <Check className="w-3 h-3" />
          </span>
        )}
      </div>

      <div className="absolute inset-x-0 bottom-0 z-10 p-3">
        <div className="text-sm sm:text-[16px] font-semibold leading-[1.05] drop-shadow"
          style={{ fontFamily: 'var(--st-font-heading)', color: '#fff' }}>
          {label}
        </div>
        <div className="text-xs mt-0.5 leading-snug line-clamp-2"
          style={{ color: 'rgba(255,255,255,0.78)' }}>
          {blurb}
        </div>
        <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold transition-opacity opacity-0 group-hover:opacity-100"
          style={{ color: TINT_INK }}>
          <span className="w-4 h-4 rounded-full inline-flex items-center justify-center"
            style={{ background: TINT_FILL, color: '#fff' }}>
            <Play className="w-2 h-2 ml-px" />
          </span>
          Commission
        </div>
      </div>
    </button>
  );
}

function LanguageGrid({ languages, selected, onSelect }) {
  return (
    <div data-testid="narrate-language-grid">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="text-[15px] sm:text-[17px] font-semibold "
          style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
          Choose narration language
        </h3>
        <p className="text-xs font-semibold "
          style={{ color: 'var(--st-mute)' }}>
          {languages.length} languages
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
        {languages.map(l => {
          const isSel = selected?.id === l.id;
          return (
            <button key={l.id} onClick={() => onSelect(l)}
              data-testid={`narrate-lang-${l.id}`}
              aria-pressed={isSel}
              className="text-left px-3.5 py-2.5 inline-flex items-center gap-3 transition "
              style={{
                borderRadius: 12,
                background: isSel
                  ? `color-mix(in srgb, ${TINT} 12%, var(--st-card))`
                  : 'var(--st-card)',
                border:    `1.5px solid ${isSel ? TINT : 'var(--st-border)'}`,
                color:     'var(--st-ink)',
              }}>
              <span className="text-[18px] leading-none">{l.flag}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold truncate"
                  style={{ fontFamily: 'var(--st-font-heading)' }}>
                  {l.native}
                </span>
                <span className="block text-xs font-semibold truncate"
                  style={{ color: 'var(--st-mute)' }}>
                  {l.label}
                </span>
              </span>
              {isSel && (
                <span className="w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0"
                  style={{ background: TINT_FILL, color: '#fff' }}>
                  <Check className="w-3 h-3" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Confirm({ genre, language, duration, setDuration, generating, onGenerate, ctaLabel }) {
  const accent = genre.accent || genre.gradient?.[0] || TINT;
  return (
    <div className="space-y-5" data-testid="narrate-confirm">
      {/* Clean designed brief card — soft accent stripe, serif title,
          generous breathing room. Sits on the light surface like a real
          studio commission sheet. */}
      <div className="relative overflow-hidden"
        style={{
          borderRadius: 16,
          background:   'var(--st-card)',
          border:      `1.5px solid var(--st-border)`,
          boxShadow:    '0 8px 24px -16px rgba(15,23,42,0.18)',
        }}>
        <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-1"
          style={{ background: accent }} />
        <div className="p-6 sm:p-7">
          <div className="text-xs font-semibold "
            style={{ color: accent }}>
            Commission Brief · {language.flag} {language.native}
          </div>
          <h3 className="text-[26px] sm:text-[32px] font-semibold leading-[1.05] mt-1.5"
            style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
            {genre.label}
            {genre._kids_band_label && (
              <span className="block sm:inline sm:ml-3 text-[17px] font-semibold align-baseline"
                style={{ color: 'var(--st-mute)' }}>
                · {genre._kids_band_label}
              </span>
            )}
          </h3>
          <p className="text-xs mt-2 max-w-2xl leading-relaxed"
            style={{ color: 'var(--st-mute)' }}>
            {genre.blurb}
          </p>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold mb-2"
          style={{ color: 'var(--st-mute)' }}>
          Runtime
        </div>
        <div className="inline-flex p-1 rounded-full"
          style={{ background: 'var(--st-card)', border: '1px solid var(--st-border)' }}>
          {DURATIONS.map(d => {
            const on = duration === d.id;
            return (
              <button key={d.id} onClick={() => setDuration(d.id)}
                data-testid={`narrate-duration-${d.id}`}
                className="px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-baseline gap-1.5 transition-colors"
                style={{
                  background: on ? TINT_FILL : 'transparent',
                  color:      on ? '#fff' : 'var(--st-ink)',
                }}>
                {d.label}
                <span className="font-normal opacity-70">{d.hint}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="pt-3 border-t flex items-center justify-between gap-4 flex-wrap"
        style={{ borderColor: 'var(--st-border)' }}>
        <div className="text-xs leading-relaxed max-w-md"
          style={{ color: 'var(--st-mute)' }}>
          <div className="text-xs font-semibold mb-1"
            style={{ color: TINT_INK }}>
            Cost · scales with narration length (~15 Super Credits typical)
          </div>
          Refunded on failure. AI writes the script, ElevenLabs voices it,
          genre sound design layered in for the final master.
        </div>
        <button onClick={onGenerate} disabled={generating}
          data-testid="narrate-generate"
          className="inline-flex items-center gap-2 px-6 py-3 text-xs font-semibold transition-transform  disabled:opacity-50"
          style={{
            background:   TINT_FILL,
            color:        '#fff',
            borderRadius: 999,
            boxShadow:    `0 12px 28px -10px ${TINT}88`,
          }}>
          {generating && <InlineSparkle label="Recording" />}
          {generating ? 'Recording' : (ctaLabel || `Commission ${genre.label}`)}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Player: layered audio (TTS + ambient loop + intro sting) ──────────────
const NARRATE_PHASES = [
  { id: 'queued',    label: 'Queued',            sub: 'Reserving a spot in the studio' },
  { id: 'scripting', label: 'Writing the script', sub: 'Turning the lesson into a story' },
  { id: 'narrating', label: 'Recording narration', sub: 'Casting a voice and laying down audio' },
  { id: 'ready',     label: 'Finishing up',       sub: 'Mastering the final cut' },
];

function Generating({ gen }) {
  const meta = gen.meta || {};
  const prog = gen.progress || {};
  const phase = prog.phase || 'queued';
  const idx = Math.max(0, NARRATE_PHASES.findIndex(p => p.id === phase));
  const current = NARRATE_PHASES[idx] || NARRATE_PHASES[0];
  const total = prog.total_chapters || 0;
  const done = prog.chapters_done || 0;
  const pct = phase === 'queued' ? 5
    : phase === 'scripting' ? 25
    : phase === 'narrating' ? 45 + (total ? Math.round((done / total) * 50) : 0)
    : 100;

  return (
    <div data-testid="narrate-generating" className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: TINT, background: 'var(--st-card)' }}>
      <div className="p-6 sm:p-8 text-center" style={{ background: `linear-gradient(135deg, ${TINT_FILL}, color-mix(in srgb, ${TINT} 45%, #000))` }}>
        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur mx-auto mb-3 flex items-center justify-center">
          <InlineSparkle size={40} label="Recording narration" />
        </div>
        <div className="text-white text-[15px] font-semibold">{current.label}…</div>
        <div className="text-white/80 text-xs mt-1">{current.sub}</div>
        {phase === 'narrating' && total > 0 && (
          <div className="text-white/70 text-xs mt-1">{done} of {total} chapters narrated</div>
        )}
      </div>
      <div className="p-4">
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--st-border)' }}>
          <div className="h-full rounded-full transition" style={{ width: `${pct}%`, background: TINT }} />
        </div>
        <div className="text-xs mt-3 text-center" style={{ color: 'var(--st-mute)' }}>
          {meta.genre_label ? `${meta.genre_label} narration` : 'Narration'} in progress — this keeps
          cooking even if you close this tab, so feel free to come back later.
        </div>
      </div>
    </div>
  );
}

function FailedCard({ gen, onReset }) {
  const reason = gen?.progress?.error || 'Something went wrong while narrating this lesson.';
  return (
    <div data-testid="narrate-failed" className="rounded-2xl border-2 p-8 text-center" style={{ borderColor: '#DC2626', background: 'var(--st-card)' }}>
      <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: '#DC2626' }} />
      <div className="text-sm font-semibold" style={{ color: 'var(--st-ink)' }}>Narration failed</div>
      <div className="text-xs mt-1.5" style={{ color: 'var(--st-mute)' }}>{reason}</div>
      <button onClick={onReset} data-testid="narrate-failed-reset"
        className="mt-4 rounded-xl px-4 py-2 text-xs font-semibold" style={{ background: TINT_FILL, color: '#fff' }}>
        Back to Setup
      </button>
    </div>
  );
}

// Player used to always receive a fully-ready generation — the backend
// awaited the whole script+TTS+SFX pipeline inline before ever writing a
// DB row. Now that narrate_generate persists a status="generating"
// placeholder immediately and finishes the work in a detached background
// task (so the narration survives the student closing the tab/browser —
// see student_narrate_routes.py), GenerationsShell can hand this an
// in-flight or failed doc too. A thin wrapper with NO hooks of its own
// dispatches to the right body — PlayerBody's ~15 hooks must never be
// reachable while status is still "generating", since the SAME mounted
// instance transitions generating -> ready via polling without
// unmounting, and an early-return placed AFTER those hooks would change
// the hook count between renders (a Rules-of-Hooks violation). Mirrors
// DeskeynomicsView.jsx's Reader/ReaderBody split.
function Player({ gen, ctx }) {
  if (gen?.status === 'generating') return <Generating gen={gen} />;
  if (gen?.status === 'failed') return <FailedCard gen={gen} onReset={() => ctx?.setActiveGen?.(null)} />;
  return <PlayerBody gen={gen} />;
}

function PlayerBody({ gen }) {
  const c = gen.content || {};
  const m = gen.meta || {};
  const engine = c.engine || 'browser-speech';
  const useEleven = engine === 'elevenlabs';
  const genreAccent = m.genre_accent || TINT;
  const genreGradient = m.genre_gradient && m.genre_gradient.length >= 2
    ? m.genre_gradient
    : [genreAccent, genreAccent];

  const chapters = useMemo(() => (c.chapters || [])
    .filter(ch => (ch.narration || '').trim())
    .map((ch, i) => ({
      title: ch.title || `Chapter ${i + 1}`,
      text: ch.narration || '',
      audio_url: ch.audio_path ? `${API}/student/generations/${gen.id}/narrate/audio/${i}` : null,
      scene_cues: (ch.scene_cues || [])
        .map((cu, j) => ({
          prompt: cu.prompt || '',
          position: cu.position || 'middle',
          url: cu.sfx_path ? `${API}/student/generations/${gen.id}/narrate/scene-sfx/${i}/${j}` : null,
        }))
        .filter(cu => cu.url),
    })), [c, gen.id]);

  const sfxIntroUrl   = m.sfx_intro   && m.genre ? `${API}/student/narrate/sfx/${m.genre}/intro`   : null;
  const sfxAmbientUrl = m.sfx_ambient && m.genre ? `${API}/student/narrate/sfx/${m.genre}/ambient` : null;

  const [chapterIdx, setChapterIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [rate, setRate] = useState(1.0);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [ambient, setAmbient] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [volume, setVolume] = useState(1.0);
  const [muted, setMuted] = useState(false);
  const [alignment, setAlignment] = useState(null);  // { chars, starts, ends, narration }
  const audioRef = useRef(null);
  const ambientRef = useRef(null);
  const stingRef = useRef(null);
  const cueRefs = useRef({});  // { '0:0': HTMLAudioElement, ... } for current chapter
  const playedCues = useRef(new Set());
  const lyricsContainerRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [ambientBlob, setAmbientBlob] = useState(null);
  const [stingBlob, setStingBlob] = useState(null);
  const [cueBlobs, setCueBlobs] = useState({});  // { '0:0': blob_url }

  // ── Fetch character-level timestamps for the active chapter ──────────────
  // ElevenLabs returns per-character start/end seconds; we use them to render
  // the narration like Spotify lyrics: the current line + word are highlighted
  // and the panel auto-scrolls to keep the active line visible.
  useEffect(() => {
    if (!useEleven || !m.has_timestamps) { setAlignment(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/student/generations/${gen.id}/narrate/timestamps/${chapterIdx}`,
          { headers: { Authorization: `Bearer ${tok()}` } },
        );
        if (cancelled) return;
        const a = r.data || {};
        if (!a.chars || !a.starts || !a.ends) { setAlignment(null); return; }
        setAlignment({
          chars: a.chars, starts: a.starts, ends: a.ends,
          narration: a.narration || chapters[chapterIdx]?.text || '',
        });
      } catch { if (!cancelled) setAlignment(null); }
    })();
    return () => { cancelled = true; };
  }, [chapterIdx, gen.id, m.has_timestamps, useEleven, chapters]);

  // ── Build word + line spans from the alignment ──────────────────────────
  // `words` is a flat list of {text, start, end, lineIdx}; `lines` groups them
  // by sentence so we can highlight the active line as a unit.
  const { words, lines } = useMemo(() => {
    if (!alignment || !alignment.chars?.length) return { words: [], lines: [] };
    const { chars, starts, ends } = alignment;
    const ws = [];
    let buf = '', wstart = null, wend = null;
    const pushWord = () => {
      if (buf) {
        ws.push({ text: buf, start: wstart ?? 0, end: wend ?? 0, lineIdx: -1 });
        buf = ''; wstart = null; wend = null;
      }
    };
    for (let i = 0; i < chars.length; i++) {
      const ch = chars[i];
      if (/\s/.test(ch)) { pushWord(); }
      else { if (wstart == null) wstart = starts[i]; wend = ends[i]; buf += ch; }
    }
    pushWord();
    // Group words into lines by sentence-ending punctuation
    let lineIdx = 0;
    const ls = [[]];
    ws.forEach((w, i) => {
      w.lineIdx = lineIdx;
      ls[lineIdx].push(w);
      const last = w.text[w.text.length - 1];
      const nextStartsCap = i + 1 < ws.length && /^[A-Z\u0900-\u0FFF\u4E00-\u9FFF]/.test(ws[i + 1].text[0] || '');
      if (/[.!?…।]/.test(last) && (nextStartsCap || ls[lineIdx].length > 14)) {
        lineIdx += 1; ls.push([]);
      } else if (ls[lineIdx].length > 18) {
        lineIdx += 1; ls.push([]);
      }
    });
    return { words: ws, lines: ls.filter(l => l.length) };
  }, [alignment]);

  const activeWordIdx = useMemo(() => {
    if (!words.length) return -1;
    if (pos <= words[0].start) return 0;
    // Binary-search the first word whose start <= pos < next.start
    let lo = 0, hi = words.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (words[mid].start <= pos) lo = mid; else hi = mid - 1;
    }
    return lo;
  }, [pos, words]);
  const activeLineIdx = words[activeWordIdx]?.lineIdx ?? -1;

  // Auto-scroll active line into view when it changes
  useEffect(() => {
    const c = lyricsContainerRef.current; if (!c) return;
    const el = c.querySelector(`[data-line-idx="${activeLineIdx}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeLineIdx]);

  // Fetch an auth-protected mp3 as a blob URL
  const fetchBlob = async (url) => {
    const r = await axios.get(url, { responseType: 'blob', headers: { Authorization: `Bearer ${tok()}` } });
    return URL.createObjectURL(r.data);
  };

  // Load chapter MP3
  useEffect(() => {
    if (!useEleven) return;
    const ch = chapters[chapterIdx];
    if (!ch?.audio_url) { setBlobUrl(null); return; }
    let revoked = false; let currentUrl = null;
    (async () => {
      try {
        currentUrl = await fetchBlob(ch.audio_url);
        if (revoked) { URL.revokeObjectURL(currentUrl); return; }
        setBlobUrl(currentUrl);
      } catch { setBlobUrl(null); }
    })();
    return () => { revoked = true; if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [chapters, chapterIdx, useEleven]);

  // Preload ambient + sting once
  useEffect(() => {
    let amb = null, sting = null;
    (async () => {
      try { if (sfxAmbientUrl) { amb = await fetchBlob(sfxAmbientUrl);   setAmbientBlob(amb); } } catch { /* optional */ }
      try { if (sfxIntroUrl)   { sting = await fetchBlob(sfxIntroUrl);   setStingBlob(sting); } } catch { /* optional */ }
    })();
    return () => {
      if (amb) URL.revokeObjectURL(amb);
      if (sting) URL.revokeObjectURL(sting);
    };
  }, [sfxAmbientUrl, sfxIntroUrl]);

  // Preload scene cues for the current chapter as blob URLs
  useEffect(() => {
    if (!useEleven) return;
    const cues = chapters[chapterIdx]?.scene_cues || [];
    if (cues.length === 0) { setCueBlobs({}); return; }
    let cancelled = false;
    const created = [];
    (async () => {
      const next = {};
      for (let j = 0; j < cues.length; j++) {
        try {
          const blob = await fetchBlob(cues[j].url);
          if (cancelled) { URL.revokeObjectURL(blob); return; }
          next[`${chapterIdx}:${j}`] = blob;
          created.push(blob);
        } catch { /* skip */ }
      }
      if (!cancelled) setCueBlobs(next);
    })();
    return () => { cancelled = true; created.forEach(b => URL.revokeObjectURL(b)); };
  }, [chapters, chapterIdx, useEleven]);

  // Reset which cues have already played whenever the chapter changes
  useEffect(() => { playedCues.current = new Set(); }, [chapterIdx]);

  // Sync rate / volume / muted to audio element
  useEffect(() => {
    if (!useEleven) return;
    const a = audioRef.current; if (!a) return;
    a.playbackRate = rate;
    a.volume = volume;
    a.muted = muted;
  }, [rate, volume, muted, useEleven, blobUrl]);

  // Helpers for ±15s skip and volume actions
  const skipBy = useCallback((delta) => {
    const a = audioRef.current; if (!a) return;
    a.currentTime = Math.max(0, Math.min((a.duration || 0) - 0.2, a.currentTime + delta));
  }, []);
  const toggleMute = useCallback(() => setMuted(v => !v), []);

  const onTimeUpdate = () => {
    const a = audioRef.current; if (!a) return;
    const ctime = a.currentTime || 0;
    const cdur = a.duration || 0;
    setPos(ctime); setDur(cdur);
    // Schedule scene-cue overlays based on chapter position fractions
    if (cdur > 0) {
      const cues = chapters[chapterIdx]?.scene_cues || [];
      cues.forEach((c, j) => {
        const frac = c.position === 'intro' ? 0.05 : c.position === 'outro' ? 0.92 : 0.5;
        const target = frac * cdur;
        const key = `${chapterIdx}:${j}`;
        if (ctime >= target && !playedCues.current.has(key)) {
          playedCues.current.add(key);
          const el = cueRefs.current[key];
          if (el) {
            try {
              el.volume = 0.55;
              el.currentTime = 0;
              el.play().catch(() => {});
            } catch (_) { /* noop */ }
          }
        }
      });
    }
  };
  const onEnded = () => {
    setPlaying(false); setPaused(false);
    if (ambientRef.current) { try { ambientRef.current.pause(); } catch (_) { /* noop */ } }
    if (chapterIdx < chapters.length - 1) {
      setChapterIdx(chapterIdx + 1);
      setTimeout(() => playFromIntro(), 80);
    }
  };

  // Play a chapter: intro sting → main TTS + ambient loop
  const playFromIntro = () => {
    // Start ambient (looping) if enabled
    if (ambient && ambientRef.current && ambientBlob) {
      try {
        ambientRef.current.volume = 0.18;
        ambientRef.current.loop = true;
        ambientRef.current.currentTime = 0;
        ambientRef.current.play().catch(() => {});
      } catch (_) { /* noop */ }
    }
    // Play sting then kick off main audio when it ends (or immediately if missing)
    const startMain = () => {
      const a = audioRef.current;
      if (!a) return;
      a.play().then(() => { setPlaying(true); setPaused(false); }).catch(() => {});
    };
    if (stingBlob && stingRef.current) {
      try {
        stingRef.current.volume = 0.75;
        stingRef.current.currentTime = 0;
        const onDone = () => { stingRef.current?.removeEventListener('ended', onDone); startMain(); };
        stingRef.current.addEventListener('ended', onDone);
        stingRef.current.play().catch(() => startMain());
      } catch (_) { startMain(); }
    } else {
      startMain();
    }
  };

  const play = () => useEleven ? playFromIntro() : null;
  const pauseFn = () => {
    if (!useEleven) return;
    audioRef.current?.pause();
    if (ambientRef.current) { try { ambientRef.current.pause(); } catch (_) { /* noop */ } }
    setPaused(true); setPlaying(false);
  };
  const stopFn = () => {
    if (!useEleven) return;
    const a = audioRef.current; if (a) { a.pause(); a.currentTime = 0; }
    if (ambientRef.current) { try { ambientRef.current.pause(); ambientRef.current.currentTime = 0; } catch (_) { /* noop */ } }
    setPlaying(false); setPaused(false);
  };
  const next = () => {
    const ni = Math.min(chapters.length - 1, chapterIdx + 1);
    setChapterIdx(ni);
    if (useEleven) setTimeout(() => playFromIntro(), 120);
  };
  const prev = () => {
    const pi = Math.max(0, chapterIdx - 1);
    setChapterIdx(pi);
    if (useEleven) setTimeout(() => playFromIntro(), 120);
  };

  const seekTo = (e) => {
    if (!useEleven) return;
    const a = audioRef.current; if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * dur;
  };

  const toggleAmbient = () => {
    const nextOn = !ambient;
    setAmbient(nextOn);
    if (ambientRef.current) {
      if (nextOn && playing) { ambientRef.current.play().catch(() => {}); }
      else { try { ambientRef.current.pause(); } catch (_) { /* noop */ } }
    }
  };

  const downloadAudio = async () => {
    if (downloading) return;
    setDownloading(true);
    const t = toast.loading('Stitching audiobook…');
    let dlUrl = null;
    try {
      const r = await fetch(
        `${API}/student/generations/${gen.id}/narrate/download`,
        { headers: { Authorization: `Bearer ${tok()}` } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      // Pull a sensible filename out of the Content-Disposition header.
      // We prefer the RFC 5987 `filename*=UTF-8''<encoded>` form so non-ASCII
      // titles (Tamil, Hindi, Chinese) survive intact.
      let filename = `${(gen.title || 'narration').replace(/[^\w]+/g, '_')}.mp3`;
      const cd = r.headers.get('content-disposition') || '';
      const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
      const plain = /filename=\"?([^\";]+)\"?/i.exec(cd);
      if (star && star[1]) {
        try { filename = decodeURIComponent(star[1]); } catch { /* fallback */ }
      } else if (plain && plain[1]) {
        filename = plain[1];
      }
      // Append to DOM so the click actually triggers the save dialog
      // across all browsers (Firefox in particular requires this).
      dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_) { /* noop */ }
        if (dlUrl) URL.revokeObjectURL(dlUrl);
      }, 1500);
      toast.success('Audiobook downloaded', { id: t });
    } catch (e) {
      console.error('narrate download failed', e);
      toast.error(friendlyError(e, 'Download failed. Please try again.'), { id: t });
      if (dlUrl) URL.revokeObjectURL(dlUrl);
    } finally {
      setDownloading(false);
    }
  };

  const shareNarration = async () => {
    let token = shareLink;
    try {
      if (!token) {
        const r = await axios.post(
          `${API}/student/generations/${gen.id}/share`,
          {}, { headers: { Authorization: `Bearer ${tok()}` } },
        );
        token = r.data?.share_token;
        setShareLink(token);
      }
      const url = `${window.location.origin}/share/narrate/${token}`;
      if (navigator.share) {
        try {
          await navigator.share({
            title: gen.title || 'Audiobook from Desky',
            text: `Listen to "${gen.title}" — a ${m.genre_label || 'Super Narrate'} audiobook.`,
            url,
          });
          return;
        } catch { /* fall through to clipboard */ }
      }
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch {
      toast.error('Could not create share link');
    }
  };

  const totalChapters = chapters.length || 1;
  const chapterProgress = useEleven && dur > 0 ? (pos / dur) : (playing ? 0.5 : 0);
  const overallProgress = ((chapterIdx + chapterProgress) / totalChapters) * 100;
  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const mm = Math.floor(s / 60); const ss = Math.floor(s % 60);
    return `${mm}:${String(ss).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4" data-testid="narrate-body">
      <Hero gen={gen} tint={genreAccent} icon={<Mic className="w-5 h-5" />} />

      {/* Hidden audio elements */}
      {useEleven && blobUrl && (
        <audio ref={audioRef} src={blobUrl}
          onTimeUpdate={onTimeUpdate} onLoadedMetadata={onTimeUpdate}
          onEnded={onEnded}
          onPlay={() => { setPlaying(true); setPaused(false); }}
          onPause={() => { if (audioRef.current && !audioRef.current.ended) setPaused(true); }}
          preload="auto" data-testid="narrate-audio" />
      )}
      {ambientBlob && <audio ref={ambientRef} src={ambientBlob} loop preload="auto" />}
      {stingBlob   && <audio ref={stingRef}   src={stingBlob}       preload="auto" />}
      {/* Per-chapter scene-cue overlays — refs keyed by `${chapterIdx}:${j}` */}
      {(chapters[chapterIdx]?.scene_cues || []).map((cu, j) => {
        const key = `${chapterIdx}:${j}`;
        const src = cueBlobs[key];
        if (!src) return null;
        return (
          <audio key={key}
            ref={el => { if (el) cueRefs.current[key] = el; }}
            src={src} preload="auto"
            data-testid={`narrate-cue-${chapterIdx}-${j}`} />
        );
      })}

      {/* Player card */}
      <div className="rounded-2xl border p-5 space-y-4" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        <div className="flex flex-wrap items-center gap-2">
          {m.genre_label && (
            <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white"
              style={{ background: `linear-gradient(135deg, ${genreGradient[0]}, ${genreGradient[1]})` }}>
              <span className="text-base leading-none">{m.genre_emoji}</span>{m.genre_label}
            </div>
          )}
          {m.audience === 'kids' && m.kids_band_label && (
            <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border"
              style={{ borderColor: 'var(--st-border)' }}
              data-testid="narrate-kids-band-chip">
              <span className="text-base leading-none">{m.kids_band_emoji}</span>{m.kids_band_label}
            </div>
          )}
          {m.language_label && (
            <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border"
              style={{ borderColor: 'var(--st-border)' }}>
              {m.language_native || m.language_label}
            </div>
          )}
          {useEleven && (
            <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{ background: `color-mix(in srgb, ${genreAccent} 14%, transparent)`, color: genreAccent }}>
              <Sparkles className="w-3 h-3" /> {m.voice_name || 'ElevenLabs'}
            </div>
          )}
          {ambientBlob && (
            <button onClick={toggleAmbient} data-testid="narrate-ambient-toggle"
              className="ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border transition-colors"
              style={{ borderColor: 'var(--st-border)', color: ambient ? genreAccent : 'var(--st-mute)' }}
              aria-pressed={ambient}>
              {ambient ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
              {ambient ? 'Ambient on' : 'Ambient off'}
            </button>
          )}
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={prev} disabled={chapterIdx === 0}
            data-testid="narrate-prev"
            className="w-10 h-10 rounded-full border flex items-center justify-center disabled:opacity-30"
            style={{ borderColor: 'var(--st-border)' }}>
            <SkipBack className="w-4 h-4" />
          </button>
          <button onClick={() => skipBy(-15)} disabled={!useEleven || !blobUrl}
            data-testid="narrate-back-15"
            title="Back 15 seconds"
            className="w-10 h-10 rounded-full border flex items-center justify-center disabled:opacity-30 relative"
            style={{ borderColor: 'var(--st-border)' }}>
            <Rewind className="w-4 h-4" />
            <span className="absolute -bottom-3 text-xs font-semibold opacity-70">15</span>
          </button>
          {playing && !paused ? (
            <button onClick={pauseFn} data-testid="narrate-pause"
              className="w-14 h-14 rounded-full text-white flex items-center justify-center transition-transform "
              style={{ background: genreAccent }}>
              <Pause className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={play} disabled={useEleven ? !blobUrl : true}
              data-testid="narrate-play"
              className="w-14 h-14 rounded-full text-white flex items-center justify-center transition-transform disabled:opacity-50"
              style={{ background: genreAccent }}>
              <Play className="w-5 h-5 ml-0.5" />
            </button>
          )}
          <button onClick={() => skipBy(15)} disabled={!useEleven || !blobUrl}
            data-testid="narrate-fwd-15"
            title="Forward 15 seconds"
            className="w-10 h-10 rounded-full border flex items-center justify-center disabled:opacity-30 relative"
            style={{ borderColor: 'var(--st-border)' }}>
            <FastForward className="w-4 h-4" />
            <span className="absolute -bottom-3 text-xs font-semibold opacity-70">15</span>
          </button>
          <button onClick={stopFn} disabled={!playing && !paused}
            data-testid="narrate-stop"
            className="w-10 h-10 rounded-full border flex items-center justify-center disabled:opacity-30"
            style={{ borderColor: 'var(--st-border)' }}>
            <Square className="w-3.5 h-3.5" />
          </button>
          <button onClick={next} disabled={chapterIdx >= chapters.length - 1}
            data-testid="narrate-next"
            className="w-10 h-10 rounded-full border flex items-center justify-center disabled:opacity-30"
            style={{ borderColor: 'var(--st-border)' }}>
            <SkipForward className="w-4 h-4" />
          </button>

          <div className="flex-1 min-w-[200px]">
            <div className="text-xs font-semibold flex items-center justify-between" style={{ color: 'var(--st-mute)' }}>
              <span>Chapter {chapterIdx + 1} / {chapters.length}</span>
              {useEleven && <span className="font-mono">{fmt(pos)} / {fmt(dur)}</span>}
              {!useEleven && <span>~{m.estimated_minutes || '?'} min</span>}
            </div>
            <div onClick={seekTo}
              className={`h-2 rounded-full mt-1.5 overflow-hidden ${useEleven ? 'cursor-pointer' : ''}`}
              style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }}
              data-testid="narrate-progress">
              <div className="h-full transition-[width] duration-200"
                style={{ width: `${overallProgress}%`, background: `linear-gradient(90deg, ${genreGradient[0]}, ${genreGradient[1]})` }} />
            </div>
            <div className="text-xs font-semibold mt-1.5 truncate" style={{ fontFamily: 'var(--st-font-heading)' }}>{chapters[chapterIdx]?.title || '—'}</div>
          </div>

          <button onClick={toggleMute}
            data-testid="narrate-mute"
            title={muted ? 'Unmute' : 'Mute'}
            className="w-9 h-9 rounded-full border flex items-center justify-center"
            style={{ borderColor: 'var(--st-border)' }}>
            {muted || volume < 0.05 ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>
          <input type="range" min="0" max="1" step="0.05"
            value={muted ? 0 : volume}
            onChange={e => { setVolume(parseFloat(e.target.value)); if (muted) setMuted(false); }}
            data-testid="narrate-volume"
            className="w-20 accent-current"
            style={{ accentColor: genreAccent }} />
          <select value={rate} onChange={e => setRate(parseFloat(e.target.value))}
            data-testid="narrate-rate"
            className="text-xs rounded-lg px-2 py-1.5 outline-none border"
            style={{ background: 'var(--st-bg)', borderColor: 'var(--st-border)', color: 'var(--st-ink)' }}>
            {[0.75, 0.9, 1.0, 1.15, 1.3, 1.5].map(r => <option key={r} value={r}>{r}×</option>)}
          </select>

          <button onClick={downloadAudio} disabled={downloading || !useEleven}
            data-testid="narrate-download-audio"
            className="rounded-full px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-50 transition-transform "
            style={{ background: genreAccent }}>
            <Download className="w-3.5 h-3.5" /> {downloading ? 'Stitching…' : 'Download'}
          </button>
          <button onClick={shareNarration}
            data-testid="narrate-share"
            className="rounded-full px-3 py-1.5 text-xs font-semibold border inline-flex items-center gap-1.5 transition-transform "
            style={{ borderColor: 'var(--st-border)' }}>
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>

        <div className="text-xs opacity-60">
          {useEleven
            ? `${m.audio_chapters || 0}/${m.total_chapters || chapters.length} chapters narrated${m.sfx_intro ? ' · intro sting ✓' : ''}${m.sfx_ambient ? ' · ambient bed ✓' : ''}${m.scene_cues ? ` · ${m.scene_cues} scene SFX cues 🎚️` : ''}${alignment ? ' · live lyrics ✓' : ''}`
            : 'ElevenLabs audio unavailable — showing script only.'}
        </div>
      </div>

      {/* Live lyrics — sentence/word highlight follows the narration */}
      {useEleven && alignment && lines.length > 0 && (
        <div className="rounded-2xl border overflow-hidden"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
          data-testid="narrate-lyrics">
          <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: 'var(--st-border)' }}>
            <div className="text-xs font-semibold " style={{ color: 'var(--st-mute)' }}>
              Live Lyrics · {chapters[chapterIdx]?.title}
            </div>
            <div className="text-xs opacity-60">{words.length} words · {lines.length} lines</div>
          </div>
          <div ref={lyricsContainerRef}
            className="px-6 py-5 space-y-3 max-h-[260px] overflow-y-auto scroll-smooth"
            style={{ background: `radial-gradient(circle at 20% 0%, color-mix(in srgb, ${genreAccent} 8%, transparent), transparent 60%)` }}>
            {lines.map((line, li) => {
              const isActive = li === activeLineIdx;
              const isPast = activeLineIdx > -1 && li < activeLineIdx;
              return (
                <p key={li} data-line-idx={li}
                  className="leading-relaxed transition duration-300"
                  style={{
                    fontFamily: 'var(--st-font-heading)',
                    fontSize: isActive ? '20px' : '15px',
                    fontWeight: isActive ? 800 : 600,
                    opacity: isActive ? 1 : isPast ? 0.35 : 0.55,
                    letterSpacing: isActive ? '-0.01em' : 0,
                  }}>
                  {line.map((w, wi) => {
                    const flatIdx = words.indexOf(w);
                    const isWordActive = isActive && flatIdx === activeWordIdx;
                    const isWordPast = isActive && flatIdx < activeWordIdx;
                    return (
                      <span key={wi}
                        onClick={() => {
                          const a = audioRef.current; if (a) { a.currentTime = w.start; a.play().catch(() => {}); }
                        }}
                        className="cursor-pointer transition-colors"
                        style={{
                          color: isWordActive ? genreAccent : 'inherit',
                          background: isWordActive ? `color-mix(in srgb, ${genreAccent} 18%, transparent)` : 'transparent',
                          padding: isWordActive ? '0 4px' : '0',
                          borderRadius: 6,
                          opacity: isActive && isWordPast ? 0.7 : 1,
                        }}>
                        {w.text}{wi < line.length - 1 ? ' ' : ''}
                      </span>
                    );
                  })}
                </p>
              );
            })}
          </div>
        </div>
      )}

      {/* Chapter list */}
      <div className="rounded-2xl border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="narrate-chapters">
        <div className="px-4 py-3 border-b text-xs font-semibold " style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)' }}>
          Chapters
        </div>
        <ol className="divide-y" style={{ borderColor: 'var(--st-border)' }}>
          {chapters.map((ch, i) => {
            const active = i === chapterIdx;
            return (
              <li key={i} className="px-4 py-3" style={{ background: active ? `color-mix(in srgb, ${genreAccent} 10%, transparent)` : 'transparent' }} data-testid={`narrate-chapter-${i}`}>
                <button onClick={() => { setChapterIdx(i); if (useEleven) setTimeout(() => playFromIntro(), 120); }} className="w-full text-left">
                  <div className="text-xs font-semibold inline-flex items-center gap-2" style={{ fontFamily: 'var(--st-font-heading)' }}>
                    <span className="w-5 h-5 rounded-full text-xs font-semibold text-white flex items-center justify-center shrink-0" style={{ background: genreAccent }}>{i+1}</span>
                    {ch.title}
                  </div>
                  <p className="text-xs mt-1.5 leading-relaxed line-clamp-3 opacity-90">{ch.text}</p>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
